<template>
  <div
    class="popup-holder popup-notification"
    v-if="notificationStore.showOptInPopup"
  >
    <div class="popup-wrap">
      <div class="popup-head">
        <h2>Stay Updated</h2>
        <a href="#" class="popup-close icon-close" @click="handleDismiss"></a>
      </div>
      <div class="popup-body">
        <div class="notification-icon">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="48"
            height="48"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
          >
            <path d="M6 8a6 6 0 0 1 12 0c0 7 3 9 3 9H3s3-2 3-9" />
            <path d="M10.3 21a1.94 1.94 0 0 0 3.4 0" />
          </svg>
        </div>
        <p class="notification-message">
          Get notified when your transcriptions are ready, even when you're not
          on the page. Never miss a completed transcription!
        </p>

        <div class="dont-ask-again" v-if="notificationStore.dismissCount >= 1">
          <label class="checkbox-label">
            <input type="checkbox" v-model="dontAskAgain" />
            <span>Don't ask me again</span>
          </label>
        </div>

        <ul class="btn-list">
          <li>
            <a
              href="#"
              class="btn btn-secondary-outline"
              @click="handleDismiss"
            >
              Not Now
            </a>
          </li>
          <li>
            <a
              href="#"
              class="btn"
              :class="{ 'btn-loading': notificationStore.isActivating }"
              @click="handleEnable"
            >
              <span v-if="!notificationStore.isActivating"
                >Enable Notifications</span
              >
              <span v-else>Enabling...</span>
            </a>
          </li>
        </ul>

        <p
          class="notification-hint"
          v-if="notificationStore.isPermissionDenied"
        >
          <small>
            Notifications are blocked. Please enable them in your browser
            settings.
          </small>
        </p>
      </div>
    </div>
  </div>
</template>

<script>
import { useNotificationStore } from "@/stores/notification-store";
import { useWorkSpaceStore } from "@/stores/workspace-store";
import { useAppStore } from "@/stores/app-store";
import { mapStores } from "pinia";

export default {
  name: "NotificationOptInPopup",
  data() {
    return {
      dontAskAgain: false,
    };
  },
  computed: {
    ...mapStores(useNotificationStore, useWorkSpaceStore, useAppStore),
    userUUID() {
      // Get user UUID from workspace store or auth
      return this.workspaceStore.userUUID;
    },
  },
  methods: {
    async handleEnable() {
      if (this.notificationStore.isActivating) {
        return;
      }

      // Get userUUID - we need to pass it to enable notifications
      // Use database userUUID (not Auth0 sub) to match backend Ably token
      const userUUID = this.appStore?.user?.userUUID;

      if (!userUUID) {
        console.error("[NotificationOptInPopup] No userUUID available");
        this.$toast.error("Unable to enable notifications. Please try again.");
        return;
      }

      const success =
        await this.notificationStore.enableNotifications(userUUID);

      if (success) {
        this.$toast.success(
          "Notifications enabled! You'll be notified when transcriptions complete.",
        );
      } else if (this.notificationStore.isPermissionDenied) {
        this.$toast.warning(
          "Notifications blocked. Please enable in browser settings.",
        );
      } else {
        this.$toast.error("Failed to enable notifications. Please try again.");
      }
    },
    async handleDismiss() {
      await this.notificationStore.dismissPrompt(this.dontAskAgain);
      this.dontAskAgain = false;
    },
  },
};
</script>

<style lang="scss" scoped>
.popup-notification {
  .popup-wrap {
    max-width: 420px;
  }

  .notification-icon {
    display: flex;
    justify-content: center;
    margin-bottom: 16px;

    svg {
      color: #f97316; // AudioScribe orange
    }
  }

  .notification-message {
    text-align: center;
    margin-bottom: 20px;
    color: var(--text-secondary, #6b7280);
    line-height: 1.5;
  }

  .dont-ask-again {
    margin-bottom: 16px;
    display: flex;
    justify-content: center;

    .checkbox-label {
      display: flex;
      align-items: center;
      gap: 8px;
      cursor: pointer;
      font-size: 14px;
      color: var(--text-secondary, #6b7280);

      input[type="checkbox"] {
        width: 16px;
        height: 16px;
        cursor: pointer;
      }
    }
  }

  .btn-list {
    list-style: none;
    padding: 0;
    margin: 0;
    display: flex;
    flex-direction: row;
    justify-content: center;
    gap: 12px;

    li {
      list-style: none;
      margin: 0;
      padding: 0;
    }
  }

  .notification-hint {
    text-align: center;
    margin-top: 12px;
    color: var(--text-muted, #9ca3af);
  }

  .btn-loading {
    opacity: 0.7;
    pointer-events: none;
  }
}
</style>
